import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useMenuExperimentVariant } from 'experiments/menu-variant/use-menu-experiment-variant-name';
import { DEFAULT_MENU_VARIANT_KEY, genericMenuArtifactUrl } from 'remote/constants';
import { LANGUAGES } from 'state/intl/types';
import { applyLocaleTextTransform } from 'state/unified-menu/apply-locale-text-transform';
import { IIngredients, ILocalizedMenu } from 'state/unified-menu/types';
import { appVersion } from 'utils/app-version-info';
import { getLanguage } from 'utils/intl/language';
import { fetch } from 'utils/network';

export type OneMenuCatalogEntry = ILocalizedMenu['entries'][number];

const CATALOG_REQUEST_KEY_SEPARATOR = '\u0000';
const cachedCatalogMenusByRequest = new Map<string, ILocalizedMenu>();
const inFlightCatalogRequestsByRequest = new Map<string, Promise<ILocalizedMenu>>();

const createCatalogRequestKey = (language: LANGUAGES, variantRequestKey: string) =>
  `${language}${CATALOG_REQUEST_KEY_SEPARATOR}${variantRequestKey}`;

const getCachedCatalogMenu = (requestKey: string) => cachedCatalogMenusByRequest.get(requestKey);

const normalizeIngredientComponents = (menu: ILocalizedMenu): ILocalizedMenu => {
  if (!menu.ingredients?.length) {
    return menu;
  }

  let didNormalize = false;
  const normalizedIngredients = menu.ingredients.map(ingredient => {
    const components = ingredient?.components;
    if (!components || Array.isArray(components) || typeof components !== 'object') {
      return ingredient;
    }

    const componentsByLanguage = components as Record<string, unknown>;
    const englishComponents = componentsByLanguage[LANGUAGES.en];
    const frenchComponents = componentsByLanguage[LANGUAGES.fr];
    const localeComponents = componentsByLanguage.locale;

    if (localeComponents === undefined) {
      return ingredient;
    }

    const normalizedComponents: Record<string, unknown> = {
      ...componentsByLanguage,
    };
    let ingredientDidNormalize = false;

    if (englishComponents === undefined) {
      normalizedComponents[LANGUAGES.en] = localeComponents;
      ingredientDidNormalize = true;
    }
    if (frenchComponents === undefined) {
      normalizedComponents[LANGUAGES.fr] = localeComponents;
      ingredientDidNormalize = true;
    }

    if (!ingredientDidNormalize) {
      return ingredient;
    }

    didNormalize = true;

    return {
      ...ingredient,
      components: normalizedComponents,
    } as typeof ingredient;
  });

  if (!didNormalize) {
    return menu;
  }

  return { ...menu, ingredients: normalizedIngredients as ILocalizedMenu['ingredients'] };
};

async function fetchCatalogMenuFromCdn(
  variantRequestKey: string,
  activeLanguage: LANGUAGES
): Promise<ILocalizedMenu> {
  let lastError: unknown;

  for (const variantKey of variantRequestKey.split(CATALOG_REQUEST_KEY_SEPARATOR)) {
    try {
      const result = await fetch<ILocalizedMenu>(genericMenuArtifactUrl(variantKey), {
        method: 'GET',
        headers: {
          'x-ui-version': appVersion,
        },
        textTransform: text => applyLocaleTextTransform(text, activeLanguage),
      });

      return normalizeIngredientComponents(result);
    } catch (fetchError) {
      lastError = fetchError;
    }
  }

  throw lastError instanceof Error ? lastError : new Error('Failed to load generic menu');
}

function getOrCreateCatalogRequest(
  requestKey: string,
  variantRequestKey: string,
  activeLanguage: LANGUAGES
): Promise<ILocalizedMenu> {
  const cachedMenu = getCachedCatalogMenu(requestKey);
  if (cachedMenu) {
    return Promise.resolve(cachedMenu);
  }

  const inFlightRequest = inFlightCatalogRequestsByRequest.get(requestKey);
  if (inFlightRequest) {
    return inFlightRequest;
  }

  const createdRequest = fetchCatalogMenuFromCdn(variantRequestKey, activeLanguage)
    .then(menu => {
      cachedCatalogMenusByRequest.set(requestKey, menu);
      return menu;
    })
    .finally(() => {
      inFlightCatalogRequestsByRequest.delete(requestKey);
    });

  inFlightCatalogRequestsByRequest.set(requestKey, createdRequest);
  return createdRequest;
}

// Test-only helper to keep hook tests isolated from module-level cache state.
export const resetOneMenuCatalogCacheForTests = () => {
  cachedCatalogMenusByRequest.clear();
  inFlightCatalogRequestsByRequest.clear();
};

/**
 * Store-less OneMenu catalog for menu-item deeplink preview.
 * Loads the generic menu JSON from the CloudFront CDN and maps entries by id.
 * Does not drive UnifiedMenu indexing.
 */
export function useOneMenuCatalog({ skip = false }: { skip?: boolean } = {}) {
  const activeLanguage = getLanguage();
  const menuExperimentVariant = useMenuExperimentVariant();
  const isMenuExperimentPending = menuExperimentVariant?.isPending === true;
  const resolvedVariantNames =
    menuExperimentVariant && !menuExperimentVariant.isPending
      ? menuExperimentVariant.variantNames
      : undefined;
  const resolvedVariantNamesKey = (resolvedVariantNames ?? [])
    .filter(Boolean)
    .join(CATALOG_REQUEST_KEY_SEPARATOR);
  const variantKeys = useMemo(() => {
    // Preserve all experiment candidates in order (key/value can differ), then
    // fall back to the default catalog artifact if none resolve.
    const candidates = resolvedVariantNamesKey
      ? resolvedVariantNamesKey.split(CATALOG_REQUEST_KEY_SEPARATOR)
      : [];
    if (!candidates.length) {
      return [DEFAULT_MENU_VARIANT_KEY];
    }
    return candidates.includes(DEFAULT_MENU_VARIANT_KEY)
      ? candidates
      : [...candidates, DEFAULT_MENU_VARIANT_KEY];
  }, [resolvedVariantNamesKey]);
  const variantRequestKey = variantKeys.join(CATALOG_REQUEST_KEY_SEPARATOR);
  const requestKey = createCatalogRequestKey(activeLanguage, variantRequestKey);
  const cachedMenu = getCachedCatalogMenu(requestKey);

  const [menuState, setMenuState] = useState<{ requestKey: string; menu: ILocalizedMenu }>();
  const [isFetching, setIsFetching] = useState(false);
  const [errorState, setErrorState] = useState<{ requestKey: string; error: Error }>();
  const fetchId = useRef(0);

  const shouldFetch = !skip && !isMenuExperimentPending;
  const menu = menuState?.requestKey === requestKey ? menuState.menu : cachedMenu;
  const error = errorState?.requestKey === requestKey ? errorState.error : undefined;

  useEffect(() => {
    if (!shouldFetch) {
      fetchId.current += 1;
      setIsFetching(false);
      return;
    }

    if (cachedMenu) {
      setMenuState(previous =>
        previous?.requestKey === requestKey ? previous : { requestKey, menu: cachedMenu }
      );
      setErrorState(undefined);
      setIsFetching(false);
      return;
    }

    const currentFetchId = ++fetchId.current;
    setIsFetching(true);
    setErrorState(undefined);

    void getOrCreateCatalogRequest(requestKey, variantRequestKey, activeLanguage)
      .then(result => {
        if (currentFetchId !== fetchId.current) {
          return;
        }

        setMenuState({ requestKey, menu: result });
        setIsFetching(false);
      })
      .catch(fetchError => {
        if (currentFetchId !== fetchId.current) {
          return;
        }

        setMenuState(undefined);
        setErrorState({
          requestKey,
          error:
            fetchError instanceof Error ? fetchError : new Error('Failed to load generic menu'),
        });
        setIsFetching(false);
      });
  }, [activeLanguage, cachedMenu, requestKey, shouldFetch, variantRequestKey]);

  const entriesById = useMemo(() => {
    const map = new Map<string, OneMenuCatalogEntry>();
    const entries = menu?.entries;
    if (!entries) {
      return map;
    }
    for (const entry of entries) {
      map.set(entry.id, entry);
    }
    return map;
  }, [menu?.entries]);

  const findEntry = useCallback((id: string) => entriesById.get(id), [entriesById]);

  const ingredients = useMemo(() => (menu?.ingredients as IIngredients[] | undefined) ?? [], [
    menu?.ingredients,
  ]);

  const awaitingRequestedVariant = shouldFetch && menu === undefined && error === undefined;

  return {
    entriesById,
    findEntry,
    loading: isMenuExperimentPending || isFetching || awaitingRequestedVariant,
    error,
    rootMenuEntryId: menu?.rootMenuEntryId,
    imageBaseUrl: menu?.meta?.imageBaseUrl,
    ingredients,
    entries: menu?.entries ?? [],
  };
}
